import * as React from "react";
import { useWatch } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import Message from "../../../reviewQuote/parts/Message";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
  return String(value);
};

const formatAddress = (...parts) => {
  const filtered = parts.filter((part) => part && String(part).trim());
  return filtered.length ? filtered.join(", ") : "N/A";
};

const InfoItem = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase">{label}</div>
    <div className="text-sm font-medium text-slate-900">{formatValue(value)}</div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <Card className="border-slate-200">
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

export function ReviewAndSubmit({
  isRenters,
  submitted,
  bannerText,
  producerOptions,
}) {
  const values = useWatch();
  const producerName =
    producerOptions?.find((p) => p.value === values?.step1?.producer)?.label ||
    values?.step1?.producer ||
    "N/A";
  const carriers = values?.step1?.carrier?.length
    ? values.step1.carrier.join(", ")
    : "N/A";
  const insuredName = `${values?.step2?.firstName || ""} ${
    values?.step2?.lastName || ""
  }`.trim();
  const insuredAddress = formatAddress(
    values?.step2?.address1,
    values?.step2?.address2,
    values?.step2?.city,
    values?.step2?.state,
    values?.step2?.zip
  );
  const priorAddress = formatAddress(
    values?.step2?.priorAddress,
    values?.step2?.priorCity,
    values?.step2?.priorState,
    values?.step2?.priorZip
  );
  const vehicles = values?.step3?.vehicles ?? [];
  const drivers = values?.step4?.drivers ?? [];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Review & Submit</h2>

      {submitted && bannerText && (
        <Message className="text-xl !bg-yellow-300 w-full max-w-[1000px]">
          {bannerText}
        </Message>
      )}

      <SectionCard title="Summary">
        <div className="grid gap-4 md:grid-cols-2">
          <InfoItem label="Producer" value={producerName} />
          <InfoItem label="Agent Email" value={values?.step1?.agentEmail} />
          <InfoItem
            label="Policy Effective Date"
            value={formatDate(values?.step1?.policyDate)}
          />
          <InfoItem label="Insured State" value={values?.step1?.insuredState} />
          <InfoItem label="Carriers" value={carriers} />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-slate-500 uppercase">Insured</div>
            <div className="text-sm font-medium text-slate-900">
              {insuredName || "N/A"}
            </div>
            <div className="text-xs text-slate-500">
              {values?.step2?.email || "N/A"}
            </div>
          </div>
          {!isRenters && (
            <>
              <InfoItem label="Vehicles" value={vehicles.length} />
              <InfoItem label="Drivers" value={drivers.length} />
            </>
          )}
          {isRenters && (
            <>
              <InfoItem label="Residence Type" value={values?.step2?.residenceType} />
              <InfoItem
                label="Personal Property"
                value={values?.step2?.personalPropertyAmount}
              />
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Insured Details">
        <div className="grid gap-4 md:grid-cols-2">
          <InfoItem label="Name" value={insuredName || "N/A"} />
          <InfoItem
            label="Date of Birth"
            value={formatDate(values?.step2?.dateOfBirth)}
          />
          <InfoItem label="Gender" value={values?.step2?.gender} />
          <InfoItem label="Email" value={values?.step2?.email} />
          <InfoItem label="Phone" value={values?.step2?.phone} />
          <InfoItem label="Address" value={insuredAddress} />
          <InfoItem
            label="Moved Recently"
            value={values?.step2?.movedRecently}
          />
          <InfoItem
            label="Prior Address"
            value={values?.step2?.movedRecently ? priorAddress : "N/A"}
          />
        </div>
      </SectionCard>

      {!isRenters && (
        <SectionCard title="Vehicles">
          {vehicles.length ? (
            <div className="grid gap-4 md:grid-cols-2">
              {vehicles.map((vehicle, index) => {
                const primaryUse =
                  vehicle?.primaryUse === "Other"
                    ? `${vehicle?.primaryUse} - ${vehicle?.primaryUseOther || "N/A"}`
                    : vehicle?.primaryUse;
                return (
                  <Card key={`vehicle-${index}`} className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base">
                        Vehicle {index + 1}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 md:grid-cols-2">
                      <InfoItem label="Year" value={vehicle?.year} />
                      <InfoItem label="Make" value={vehicle?.make} />
                      <InfoItem label="Model" value={vehicle?.model} />
                      <InfoItem label="VIN" value={vehicle?.vin} />
                      <InfoItem label="Garaging Zip" value={vehicle?.garagingZip} />
                      <InfoItem label="Primary Use" value={primaryUse} />
                      <InfoItem label="Annual Miles" value={vehicle?.annualMiles} />
                      <InfoItem label="Ride Share" value={vehicle?.rideShare} />
                      <InfoItem label="Delivery" value={vehicle?.delivery} />
                      <InfoItem label="Lienholder" value={vehicle?.lienholder} />
                      <InfoItem
                        label="APD Deductible Selected"
                        value={vehicle?.apdCoverage}
                      />
                      <InfoItem
                        label="APD Deductible Amount"
                        value={vehicle?.apdCoverage ? vehicle?.apdDeductible : "N/A"}
                      />
                      <InfoItem label="Lienholder Name" value={vehicle?.lienholderName} />
                      <InfoItem
                        label="Lienholder Address"
                        value={formatAddress(
                          vehicle?.lienholderAddress,
                          vehicle?.lienholderAddress2,
                          vehicle?.lienholderCity,
                          vehicle?.lienholderState,
                          vehicle?.lienholderZip
                        )}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No vehicles added.</div>
          )}
        </SectionCard>
      )}

      {!isRenters && (
        <SectionCard title="Drivers">
          {drivers.length ? (
            <div className="grid gap-4 md:grid-cols-2">
              {drivers.map((driver, index) => {
                const driverName = `${driver?.firstName || ""} ${
                  driver?.lastName || ""
                }`.trim();
                return (
                  <Card key={`driver-${index}`} className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base">
                        Driver {index + 1}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 md:grid-cols-2">
                      <InfoItem label="Name" value={driverName || "N/A"} />
                      <InfoItem
                        label="Date of Birth"
                        value={formatDate(driver?.dateOfBirth)}
                      />
                      <InfoItem label="Gender" value={driver?.gender} />
                      <InfoItem label="Marital Status" value={driver?.maritalStatus} />
                      <InfoItem label="Education" value={driver?.educationLevel} />
                      <InfoItem label="License Type" value={driver?.licenseType} />
                      <InfoItem
                        label="Licensed Duration"
                        value={driver?.licensedDuration}
                      />
                      <InfoItem label="Employment Type" value={driver?.employmentType} />
                      <InfoItem label="Employment Line" value={driver?.employmentLine} />
                      <InfoItem label="Driver Status" value={driver?.autoDriverStatus} />
                      <InfoItem label="SR-22" value={driver?.sr22} />
                      <InfoItem
                        label="Distant Student Discount"
                        value={driver?.distantStudentDiscount}
                      />
                      <InfoItem
                        label="Good Student Discount"
                        value={driver?.goodStudentDiscount}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No drivers added.</div>
          )}
        </SectionCard>
      )}

      {!isRenters && (
        <SectionCard title="Insured History">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem
              label="Prior Insurance Status"
              value={values?.step5?.priorInsuranceStatus}
            />
            <InfoItem
              label="Prior Carrier"
              value={values?.step5?.priorInsuranceCarrier}
            />
            <InfoItem
              label="Bodily Injury Limits"
              value={values?.step5?.bodilyInjuryLimits}
            />
            <InfoItem
              label="Years With Carrier"
              value={values?.step5?.yearsWithCarrier}
            />
            <InfoItem label="Drivers Listed" value={values?.step5?.driversListed} />
            <InfoItem label="PIP Claims (5 yrs)" value={values?.step5?.pipClaims} />
            <InfoItem
              label="Total Residents"
              value={values?.step5?.totalResidents}
            />
            <InfoItem
              label="Primary Residence"
              value={values?.step5?.primaryResidence}
            />
            <InfoItem label="Renters Discount" value={values?.step5?.rentersDiscount} />
          </div>
        </SectionCard>
      )}

      {isRenters && (
        <SectionCard title="Renters History">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem label="Prior Carrier" value={values?.step3?.priorCarrier} />
            <InfoItem
              label="Prior Liability Limit"
              value={values?.step3?.priorLiabilityLimit}
            />
            <InfoItem
              label="Reported Claims"
              value={values?.step3?.reportedClaims}
            />
          </div>
        </SectionCard>
      )}

      {isRenters && (
        <SectionCard title="Renters Details">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem label="Residence Type" value={values?.step2?.residenceType} />
            <InfoItem
              label="Personal Property Amount"
              value={values?.step2?.personalPropertyAmount}
            />
            <InfoItem
              label="Restricted Dogs"
              value={values?.step2?.hasRestrictedDogs}
            />
          </div>
        </SectionCard>
      )}

      {isRenters && (
        <SectionCard title="Demographic">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem
              label="First Name"
              value={values?.step2?.demographicFirstName}
            />
            <InfoItem
              label="Last Name"
              value={values?.step2?.demographicLastName}
            />
            <InfoItem
              label="Marital Status"
              value={values?.step2?.demographicMaritalStatus}
            />
            <InfoItem
              label="Date of Birth"
              value={formatDate(values?.step2?.demographicDateOfBirth)}
            />
            <InfoItem label="Gender" value={values?.step2?.demographicGender} />
          </div>
        </SectionCard>
      )}

      {isRenters && (
        <SectionCard title="Additional Person">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem label="First Name" value={values?.step2?.additionalFirstName} />
            <InfoItem label="Last Name" value={values?.step2?.additionalLastName} />
            <InfoItem
              label="Marital Status"
              value={values?.step2?.additionalMaritalStatus}
            />
            <InfoItem
              label="Relationship"
              value={values?.step2?.additionalRelationship}
            />
            <InfoItem
              label="Second Named Insured"
              value={values?.step2?.additionalSecondNamedInsured}
            />
            <InfoItem
              label="Date of Birth"
              value={formatDate(values?.step2?.additionalDateOfBirth)}
            />
            <InfoItem label="Gender" value={values?.step2?.additionalGender} />
          </div>
        </SectionCard>
      )}

      {!isRenters && (
        <SectionCard title="Coverage Highlights">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem
              label="Bodily Injury & Property Damage"
              value={values?.step6?.bodilyInjury}
            />
            <InfoItem
              label="UM/UIM Property Damage"
              value={values?.step6?.umPropertyDamage}
            />
            <InfoItem label="Med Pay" value={values?.step6?.medPay} />
            <InfoItem label="PIP" value={values?.step6?.pip} />
            <InfoItem
              label="Comp Deductible"
              value={values?.step6?.compDeductible}
            />
            <InfoItem
              label="Collision Deductible"
              value={values?.step6?.collisionDeductible}
            />
            <InfoItem label="Payment Plan" value={values?.step6?.paymentPlan} />
            <InfoItem label="Run Clue/MVR" value={values?.step6?.runClue} />
          </div>
        </SectionCard>
      )}

      {isRenters && (
        <SectionCard title="Coverage Highlights">
          <div className="grid gap-4 md:grid-cols-2">
            <InfoItem label="Liability Limit" value={values?.step4?.liabilityLimit} />
            <InfoItem
              label="Medical Payments Limit"
              value={values?.step4?.medicalPaymentsLimit}
            />
            <InfoItem label="Deductible" value={values?.step4?.deductible} />
            <InfoItem label="Theft Sublimit" value={values?.step4?.theftSublimit} />
            <InfoItem
              label="Home Computer Coverage"
              value={values?.step4?.homeComputerCoverage}
            />
            <InfoItem label="Payment Plan" value={values?.step4?.paymentPlan} />
            <InfoItem label="Run Clue" value={values?.step4?.runClue} />
          </div>
        </SectionCard>
      )}
    </div>
  );
}
